import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useCallback, useLayoutEffect } from 'react';
const AR_EPSILON = 0.01;
// ---------------------------------------------------------------------------
// Percentage-based geometry — computed once from aspect ratios, not pixels.
// Children expressed as percentages scale automatically with the container,
// so only the container's pixel position needs updating on resize.
// ---------------------------------------------------------------------------
/**
 * Compute matte bar percentage dimensions.
 * Returns 2 style objects or empty array.
 */
export function computeMatteBars(videoAR, matteAR) {
    if (Math.abs(matteAR - videoAR) < AR_EPSILON)
        return [];
    if (matteAR > videoAR) {
        // Matte wider → horizontal bars (top + bottom)
        const pct = ((1 - videoAR / matteAR) / 2 * 100) + '%';
        return [
            { position: 'absolute', top: 0, left: 0, width: '100%', height: pct },
            { position: 'absolute', bottom: 0, left: 0, width: '100%', height: pct },
        ];
    }
    else {
        // Matte narrower → vertical bars (left + right)
        const pct = ((1 - matteAR / videoAR) / 2 * 100) + '%';
        return [
            { position: 'absolute', top: 0, left: 0, height: '100%', width: pct },
            { position: 'absolute', top: 0, right: 0, height: '100%', width: pct },
        ];
    }
}
/**
 * Compute safe area outline box positions as percentage-based CSS.
 * All values are percentages of the overlay container (which matches
 * the video content area), so they scale without recalculation.
 */
export function computeSafeAreaBoxes(safeArea, videoAR) {
    // Zone within the overlay where this safe area applies (percentages of overlay)
    let zoneLeftPct = 0;
    let zoneTopPct = 0;
    let zoneWidthPct = 100;
    let zoneHeightPct = 100;
    if (safeArea === '4:3') {
        const targetAR = 4 / 3;
        if (videoAR > targetAR) {
            // Overlay wider than 4:3 → 4:3 zone centered horizontally
            zoneWidthPct = (targetAR / videoAR) * 100;
            zoneLeftPct = (100 - zoneWidthPct) / 2;
        }
        else {
            // Overlay taller than 4:3 → 4:3 zone centered vertically
            zoneHeightPct = (videoAR / targetAR) * 100;
            zoneTopPct = (100 - zoneHeightPct) / 2;
        }
    }
    const actionMargin = safeArea === '16:9' ? 0.035 : 0.05;
    const titleMargin = safeArea === '16:9' ? 0.05 : 0.10;
    const actionBox = {
        position: 'absolute',
        left: (zoneLeftPct + zoneWidthPct * actionMargin) + '%',
        top: (zoneTopPct + zoneHeightPct * actionMargin) + '%',
        width: (zoneWidthPct * (1 - 2 * actionMargin)) + '%',
        height: (zoneHeightPct * (1 - 2 * actionMargin)) + '%',
    };
    const titleBox = {
        position: 'absolute',
        left: (zoneLeftPct + zoneWidthPct * titleMargin) + '%',
        top: (zoneTopPct + zoneHeightPct * titleMargin) + '%',
        width: (zoneWidthPct * (1 - 2 * titleMargin)) + '%',
        height: (zoneHeightPct * (1 - 2 * titleMargin)) + '%',
    };
    return [actionBox, titleBox];
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const VideoOverlayLayer = React.memo(function VideoOverlayLayer({ videoElement, activeMatte, activeSafeAreas, hidden = false, }) {
    const containerRef = useRef(null);
    // Imperatively reposition the container to match the video content area.
    // Runs synchronously in the ResizeObserver callback — no React state,
    // no extra render frame — so the overlay tracks the video exactly.
    const syncPosition = useCallback(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const w = videoElement.clientWidth;
        const h = videoElement.clientHeight;
        const nw = videoElement.videoWidth;
        const nh = videoElement.videoHeight;
        if (nw <= 0 || nh <= 0 || w <= 0 || h <= 0)
            return;
        const videoAR = nw / nh;
        const elementAR = w / h;
        let contentW, contentH, offsetX, offsetY;
        if (videoAR > elementAR) {
            contentW = w;
            contentH = w / videoAR;
            offsetX = 0;
            offsetY = (h - contentH) / 2;
        }
        else {
            contentH = h;
            contentW = h * videoAR;
            offsetX = (w - contentW) / 2;
            offsetY = 0;
        }
        // Floor offsets and ceil dimensions so the overlay fully covers the video
        // content area. This compensates for sub-pixel differences between our
        // calculation and the browser's object-fit: contain placement. The
        // container's overflow: hidden clips any overshoot.
        el.style.top = Math.floor(offsetY) + 'px';
        el.style.left = Math.floor(offsetX) + 'px';
        el.style.width = Math.ceil(contentW) + 'px';
        el.style.height = Math.ceil(contentH) + 'px';
    }, [videoElement]);
    useLayoutEffect(() => {
        syncPosition();
        const ro = new ResizeObserver(syncPosition);
        ro.observe(videoElement);
        return () => ro.disconnect();
    }, [videoElement, syncPosition]);
    // Re-sync when matte/safe area selection changes (structure changes)
    useLayoutEffect(syncPosition, [activeMatte, activeSafeAreas, syncPosition]);
    if (activeMatte === null && activeSafeAreas.length === 0)
        return null;
    const videoAR = videoElement.videoWidth / videoElement.videoHeight;
    // Matte bars — percentage-based, scale with container automatically
    const matteBars = activeMatte !== null
        ? computeMatteBars(videoAR, activeMatte)
        : [];
    // Safe area boxes — percentage-based
    const allSafeAreaBoxes = [];
    for (const kind of activeSafeAreas) {
        const boxes = computeSafeAreaBoxes(kind, videoAR);
        boxes.forEach((style, i) => {
            allSafeAreaBoxes.push({ kind, index: i, style });
        });
    }
    return (_jsxs("div", { ref: containerRef, className: `hekate-overlay-layer${hidden ? ' hekate-overlay-layer--hidden' : ''}`, style: {
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: 1,
            overflow: 'hidden',
        }, children: [matteBars.map((style, i) => (_jsx("div", { className: "hekate-overlay-matte-bar", style: style }, `matte-${i}`))), allSafeAreaBoxes.map(({ kind, index, style }) => (_jsx("div", { className: `hekate-overlay-safe-area ${index === 0 ? 'hekate-overlay-safe-area--action' : 'hekate-overlay-safe-area--title'}`, style: style }, `safe-${kind}-${index}`)))] }));
});
